import { SyncState } from '@/types/graphql';
import { TypedDocumentNode, gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { Check, CloudDoneOutlined, CloudOff, CloudQueue } from '@mui/icons-material';
import {
    Box,
    Button,
    Divider,
    Paper,
    Popper,
    SvgIconProps,
    Typography,
    TypographyProps,
} from '@mui/material';
import moment from 'moment';
import { ComponentType, MouseEvent, ReactNode, useRef, useState } from 'react';

import { BORDER_GRAY, WHITE } from '../../constants/colors';
import { formatDatetime } from '../../utils/datetimes/formatting';
import {
    TriggerVisitSyncFromPopoverMutation,
    TriggerVisitSyncFromPopoverMutationVariables,
} from './SyncStatusPopover.gql.types';
import { SyncDisplayState, getSyncDisplayState } from './syncDisplayState';
import { SYNC_STATE_COLOR } from './syncStatusColors';

export type SyncStatusForPopover = {
    state: SyncState;
    lastAttempt: { id: string; createdAt: Date } | null;
    lastSuccess?: { createdAt: Date } | null;
    lastFailure?: { userSafeErrorMessage: string | null } | null;
};

type SyncStatusPopoverProps = {
    visitId: string;
    syncStatus: SyncStatusForPopover;
    existsInExternalSystem: boolean;
    timezone?: string | null;
};

const NOT_YET_SYNCED_DESCRIPTION = 'Schedule this visit to sync it to your external system.';

const TRIGGER_VISIT_SYNC: TypedDocumentNode<
    TriggerVisitSyncFromPopoverMutation,
    TriggerVisitSyncFromPopoverMutationVariables
> = gql`
    mutation TriggerVisitSyncFromPopover($id: UUID!) {
        triggerVisitWebhook(visitId: $id) {
            id
        }
    }
`;

type SyncStateConfig = {
    icon: ComponentType<SvgIconProps>;
    color: string;
    label: string;
};

const SYNC_STATE_CONFIG: Record<SyncDisplayState, SyncStateConfig> = {
    [SyncDisplayState.SYNCED]: {
        icon: CloudDoneOutlined,
        color: SYNC_STATE_COLOR[SyncState.SUCCEEDING],
        label: 'Last synced',
    },
    [SyncDisplayState.FAILED]: {
        icon: CloudOff,
        color: SYNC_STATE_COLOR[SyncState.FAILING],
        label: 'Failed to sync',
    },
    [SyncDisplayState.PENDING]: {
        icon: CloudQueue,
        color: SYNC_STATE_COLOR[SyncState.PENDING],
        label: 'Sync pending',
    },
    [SyncDisplayState.NOT_YET_SYNCED]: {
        icon: CloudQueue,
        color: SYNC_STATE_COLOR[SyncState.NEVER_ATTEMPTED],
        label: 'Not yet synced',
    },
};

const MARKER_INDENT = '22px';

const Dot = ({ color }: { color: string }) => (
    <Box
        sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: color,
            flexShrink: 0,
        }}
    />
);

const MarkerColumn = ({ children }: { children: ReactNode }) => (
    <Box sx={{ width: MARKER_INDENT, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {children}
    </Box>
);

const RestingIcon = ({ display }: { display: SyncDisplayState }) => {
    const { icon: Icon, color } = SYNC_STATE_CONFIG[display];
    return <Icon sx={{ fontSize: 18, color }} />;
};

const Header = ({
    color,
    label,
    textColor,
}: {
    color: string;
    label: string;
    textColor: string;
}) => (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <MarkerColumn>
            <Dot color={color} />
        </MarkerColumn>
        <Box sx={{ fontWeight: 700, fontSize: 13, color: textColor }}>{label}</Box>
    </Box>
);

const MutedText = (props: TypographyProps) => (
    <Typography variant='caption' color='text.secondary' {...props} />
);

const StrongText = (props: TypographyProps) => (
    <Typography variant='caption' fontWeight='medium' {...props} />
);

const SyncStatusBody = ({
    syncStatus,
    display,
    timezone,
    onResync,
}: {
    syncStatus: SyncStatusForPopover;
    display: SyncDisplayState;
    timezone?: string | null;
    onResync: () => void;
}) => {
    const { lastAttempt, lastSuccess, lastFailure } = syncStatus;
    const { color, label } = SYNC_STATE_CONFIG[display];
    const isFailing = display === SyncDisplayState.FAILED;
    const isNotYetSynced = display === SyncDisplayState.NOT_YET_SYNCED;
    const formatTimestamp = (date: Date) => formatDatetime(moment(date), timezone ?? null, {});

    return (
        <>
            <Header color={color} label={label} textColor={isFailing ? color : 'text.primary'} />
            {isNotYetSynced ? (
                <MutedText sx={{ pl: MARKER_INDENT, maxWidth: 280 }}>
                    {NOT_YET_SYNCED_DESCRIPTION}
                </MutedText>
            ) : (
                lastAttempt && (
                    <MutedText sx={{ pl: MARKER_INDENT }}>
                        {formatTimestamp(lastAttempt.createdAt)}
                    </MutedText>
                )
            )}
            {isFailing && lastFailure?.userSafeErrorMessage && (
                <MutedText sx={{ pl: MARKER_INDENT, maxWidth: 280 }}>
                    {lastFailure.userSafeErrorMessage}
                </MutedText>
            )}
            {isFailing && lastSuccess && (
                <>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                        <MarkerColumn>
                            <Check
                                sx={{ fontSize: 16, color: SYNC_STATE_COLOR[SyncState.SUCCEEDING] }}
                            />
                        </MarkerColumn>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <MutedText>Last successful sync:</MutedText>
                            <StrongText>{formatTimestamp(lastSuccess.createdAt)}</StrongText>
                        </Box>
                    </Box>
                </>
            )}
            {isFailing && (
                <Button
                    variant='outlined'
                    color='inherit'
                    size='small'
                    onClick={onResync}
                    sx={{
                        alignSelf: 'flex-start',
                        mt: 0.5,
                        textTransform: 'none',
                        color: 'text.primary',
                        borderColor: BORDER_GRAY,
                        backgroundColor: WHITE,
                    }}
                >
                    Resync
                </Button>
            )}
        </>
    );
};

export const SyncStatusPopover = ({
    visitId,
    syncStatus,
    existsInExternalSystem,
    timezone,
}: SyncStatusPopoverProps) => {
    const display = getSyncDisplayState(syncStatus.state, existsInExternalSystem);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [triggerSync] = useMutation(TRIGGER_VISIT_SYNC);

    const cancelClose = () => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    };
    const open = (e: MouseEvent<HTMLElement>) => {
        cancelClose();
        setAnchorEl(e.currentTarget);
    };
    const scheduleClose = () => {
        cancelClose();
        closeTimer.current = setTimeout(() => setAnchorEl(null), 120);
    };

    const handleResync = () => {
        void triggerSync({ variables: { id: visitId } });
        setAnchorEl(null);
    };

    return (
        <>
            <Box
                component='span'
                sx={{ display: 'inline-flex', cursor: 'default' }}
                onMouseEnter={open}
                onMouseLeave={scheduleClose}
                onClick={(e) => e.stopPropagation()}
            >
                <RestingIcon display={display} />
            </Box>
            <Popper
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                placement='bottom-start'
                sx={{ zIndex: 1300 }}
            >
                <Paper
                    elevation={3}
                    onMouseEnter={cancelClose}
                    onMouseLeave={scheduleClose}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        mt: 0.5,
                        p: 1.5,
                        width: 'max-content',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                    }}
                >
                    <SyncStatusBody
                        syncStatus={syncStatus}
                        display={display}
                        timezone={timezone}
                        onResync={handleResync}
                    />
                </Paper>
            </Popper>
        </>
    );
};
